import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  MenuItem,
  Paper,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Grid,
} from '@mui/material';
import { ArrowBack, Save, VerifiedUser } from '@mui/icons-material';
import { PageHeader } from '../../components/common/pageKit';
import { useControlRoomColors } from '../../components/common/controlRoom';
import {
  customerService,
  INDIAN_STATES,
  validateGSTIN,
  getStateFromGSTIN,
  CreateCustomerRequest,
  Customer,
} from '../../services/customerService';

// ---------------------------------------------------------------------------
// Create / edit a Customer as a dedicated full page (not a modal), mirroring
// SalesContractFormPage / QuotationFormPage: titled sections, a sticky bottom
// action bar, and navigate() back to the list on save/cancel.
//
// Field set and payload shape are preserved EXACTLY from the old dialog in
// CustomerPage.tsx / QuickAddCustomer.tsx -- no new backend fields are sent.
// A couple of fields present in the task's generic template (country,
// notes/is_active) were intentionally left out: neither the old dialog nor
// customerService.CreateCustomerRequest carries them today, so adding inputs
// for them would silently invent fields the API never receives.
//
// KNOWN SERVICE QUIRK (not fixed here -- out of edit scope): customerService
// .updateCustomer() forwards its `data` argument to PUT verbatim, without the
// camelCase transform that .createCustomer() applies. The backend's
// updateCustomer controller (erp-api src/controllers/sales.js) destructures
// camelCase keys (companyName, postalCode, creditLimit, ...) from req.body,
// so passing the documented snake_case CreateCustomerRequest shape would
// silently fail validation. The update payload below is built with the
// camelCase keys the controller actually reads (matching what the original
// dialog's handleUpdateCustomer sent via a raw api.put), cast through the
// documented type so the exposed service function can still be reused as-is.
// -----------------------------------------------------------------------------

const toNum = (v: string): number | undefined => {
  if (v === '' || v === null || v === undefined) return undefined;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : undefined;
};

const CustomerFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const c = useControlRoomColors();

  const [loadingCustomer, setLoadingCustomer] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const [companyName, setCompanyName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const [gstin, setGstin] = useState('');
  const [gstinError, setGstinError] = useState<string | null>(null);

  const [creditLimit, setCreditLimit] = useState('0');
  const [creditDays, setCreditDays] = useState('30');

  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoadingCustomer(true);
    customerService
      .getCustomerById(Number(id))
      // The GET endpoint nests the row under a `customer` key (see
      // erp-api src/controllers/sales.js getCustomerById); the service's
      // return type doesn't reflect that, so unwrap defensively here.
      .then((res: Customer | { customer: Customer }) => {
        const cust: any = (res as any)?.customer ?? res;
        setCompanyName(cust.company_name || '');
        setContactPerson(cust.contact_person || '');
        setEmail(cust.email || '');
        setPhone(cust.phone || '');
        setAddress(cust.address || '');
        setCity(cust.city || '');
        setState(cust.state || '');
        // DB column is `pincode`; customerService's Customer type claims
        // `postal_code`, so accept either.
        setPostalCode(cust.pincode || cust.postal_code || '');
        setGstin(cust.gstin || '');
        setCreditLimit(cust.credit_limit != null ? String(cust.credit_limit) : '0');
        setCreditDays(cust.credit_days != null ? String(cust.credit_days) : '30');
      })
      .catch((err: any) => setError(err.message || 'Failed to load customer'))
      .finally(() => setLoadingCustomer(false));
  }, [isEdit, id]);

  const handleGstinChange = (value: string) => {
    const upper = value.toUpperCase();
    setGstin(upper);

    if (!upper) {
      setGstinError(null);
      return;
    }

    const validation = validateGSTIN(upper);
    setGstinError(validation.valid ? null : validation.error || null);

    // Auto-derive place of supply (state) from a fully valid GSTIN, same
    // logic the old dialog / QuickAddCustomer used.
    if (validation.valid && upper.length === 15) {
      const derivedState = getStateFromGSTIN(upper);
      if (derivedState) setState(derivedState);
    }
  };

  // PAN is embedded in a valid GSTIN (characters 3-12) -- display-only,
  // derived client-side, never sent as a separate field (no such field
  // exists on the customer payload today).
  const derivedPan = gstin.length === 15 && !gstinError ? gstin.substring(2, 12) : null;
  const derivedStateCode = gstin.length >= 2 ? gstin.substring(0, 2) : null;

  const validate = (): string[] => {
    const errs: string[] = [];
    if (!companyName.trim()) errs.push('Company name is required.');
    if (!contactPerson.trim()) errs.push('Contact person is required.');
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errs.push('Enter a valid email address.');
    if (gstin && gstinError) errs.push(`GSTIN: ${gstinError}`);
    const limit = toNum(creditLimit);
    if (limit !== undefined && (limit < 0 || limit > 10000000)) errs.push('Credit limit must be between 0 and 1 crore.');
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    setValidationErrors(errs);
    if (errs.length > 0) return;

    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && id) {
        const updatePayload = {
          companyName: companyName.trim(),
          contactPerson: contactPerson.trim(),
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          address: address.trim() || undefined,
          city: city.trim() || undefined,
          state: state || undefined,
          postalCode: postalCode.trim() || undefined,
          gstin: gstin || undefined,
          creditLimit: toNum(creditLimit),
          creditDays: toNum(creditDays),
          // Cast: see the KNOWN SERVICE QUIRK note above -- the controller
          // reads these exact camelCase keys, not the snake_case shape the
          // service's TS type documents.
        } as unknown as Partial<CreateCustomerRequest>;
        await customerService.updateCustomer(Number(id), updatePayload);
      } else {
        const payload: CreateCustomerRequest = {
          company_name: companyName.trim(),
          contact_person: contactPerson.trim(),
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          address: address.trim() || undefined,
          city: city.trim() || undefined,
          state: state || undefined,
          postal_code: postalCode.trim() || undefined,
          gstin: gstin || undefined,
          credit_limit: toNum(creditLimit),
          credit_days: toNum(creditDays),
        };
        await customerService.createCustomer(payload);
      }
      navigate('/sales/customers');
    } catch (err: any) {
      setError(err.message || 'Failed to save customer');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingCustomer) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 10 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/customers')} sx={{ mb: 1 }}>
        Back to Customers
      </Button>

      <PageHeader
        title={isEdit ? 'Edit Customer' : 'New Customer'}
        subtitle="Maintain the customer master with GSTIN validation and GST compliance."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((e) => (
            <Box key={e}>{e}</Box>
          ))}
        </Alert>
      )}

      {/* Section: Identity & Basic Details */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Identity &amp; Basic Details
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Company Name *"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Contact Person *"
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
              required
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Section: Tax & Compliance */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Tax &amp; Compliance
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="GSTIN"
              value={gstin}
              onChange={(e) => handleGstinChange(e.target.value)}
              placeholder="15 character GSTIN"
              error={!!gstinError}
              helperText={gstinError || 'Format: 22AAAAA0000A1Z5'}
              inputProps={{ maxLength: 15, style: { fontFamily: 'monospace' } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, justifyContent: 'center', height: '100%' }}>
              <Typography variant="caption" color="text.secondary">
                Derived from GSTIN
              </Typography>
              {derivedPan ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Chip size="small" icon={<VerifiedUser />} label={`PAN: ${derivedPan}`} sx={{ fontFamily: 'monospace' }} />
                  {state && <Chip size="small" label={`Place of Supply: ${state}${derivedStateCode ? ` (${derivedStateCode})` : ''}`} />}
                </Box>
              ) : (
                <Typography variant="body2" color="text.disabled">
                  Enter a valid GSTIN to auto-fill PAN and place of supply.
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Section: Commercial Terms */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Commercial Terms
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Credit Limit (₹)"
              type="number"
              value={creditLimit}
              onChange={(e) => setCreditLimit(e.target.value)}
              inputProps={{ min: 0, max: 10000000 }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Credit Days"
              type="number"
              value={creditDays}
              onChange={(e) => setCreditDays(e.target.value)}
              inputProps={{ min: 0, max: 365 }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Section: Address */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Address
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              label="Address"
              multiline
              rows={2}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              select
              size="small"
              label="State"
              value={state}
              onChange={(e) => setState(e.target.value)}
              helperText="Auto-filled from a valid GSTIN, or set manually."
            >
              <MenuItem value="">Select State</MenuItem>
              {INDIAN_STATES.map((s) => (
                <MenuItem key={s.code} value={s.name}>
                  {s.code} - {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="Postal Code"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              inputProps={{ maxLength: 6 }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Sticky bottom action bar */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 5,
          mt: 1,
          py: 1.5,
          px: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: c.panel,
        }}
      >
        <Button onClick={() => navigate('/sales/customers')} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSubmit}
          disabled={submitting || !companyName.trim() || !contactPerson.trim()}
        >
          {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Customer'}
        </Button>
      </Paper>
    </Box>
  );
};

export default CustomerFormPage;
